import { uniq } from '../../utils/helpers';

// https://uppy.io/docs/uppy/#restrictions

// Do not allow upload html,liquid and svg files because these extensions are blocked due to phishing protections
const permissibleTypes = {
  video: [
    'video/*'
  ],
  audio: [
    'audio/*'
  ],
  image: [
    'image/*', '.png', '.jpg', '.jpeg'
  ],
  other: [
    'application/pdf', 'audio/mpeg', '.3g2', '.3gp',
    '.avi', '.csv', '.doc', '.docx',
    '.epub', '.flv', '.gif', '.key',
    '.m4a', '.mov', '.mp3', '.mp4',
    '.mpg', '.mobi', '.odt', '.ogg',
    '.ogv', '.pdf', '.ppt', '.pptx',
    '.rtf', '.srt', '.txt', '.wav',
    '.wma', '.wmv', '.xls', '.xlsx',
    '.zip', '.gz', '.css', '.scss', '.js',
    '.ico', '.swf', '.eot', '.ttf',
    '.woff', '.woff2', '.aac', '.ai', '.als',
    '.bmp', '.dotx', '.eps', '.flp',
    '.gdoc', '.gsheet', '.heic', '.jfif',
    '.m4v', '.mid', '.mkv', '.mpeg',
    '.numbers', '.odp', '.ods', '.pages',
    '.psd', '.psfx', '.pub', '.rar',
    '.rpp', '.tif', '.tiff', '.unity',
    '.webp', '.xlsb',
    '.cer', '.p8', '.p12', '.json', '.mobileprovision'
  ]
};

export const supportedAudioMimeTypes = [
  'audio/aac',
  'audio/x-ableton-live-set',
  'application/vnd.adobe.fla',
  'audio/mp4',
  'audio/midi',
  'audio/mpeg',
  'audio/ogg',
  'audio/wav',
  'audio/x-ms-wma',
];

export const supportImageMimeTypes = [
  'image/bmp',
  'application/postscript',
  'application/vnd.google-apps.drawing',
  'image/gif',
  'image/heic',
  'image/vnd.microsoft.icon',
  'image/jpeg',
  'image/png',
  'image/vnd.adobe.photoshop',
  'image/tiff',
  'image/webp',
];

export const supportedVideoMimeTypes = [
  'video/3gpp2',
  'video/3gpp',
  'video/x-msvideo',
  'video/x-flv',
  'video/x-m4v',
  'video/x-matroska',
  'video/quicktime',
  'video/mp4',
  'video/mpeg',
  'video/ogg',
  'video/x-ms-wmv',
];

const IMAGE_EXTENSION_WILDCARD = 'image/*';
const VIDEO_EXTENSION_WILDCARD = 'video/*';
const AUDIO_EXTENSION_WILDCARD = 'audio/*';

const EXTENSION_WILDCARDS = [
  IMAGE_EXTENSION_WILDCARD,
  VIDEO_EXTENSION_WILDCARD,
  AUDIO_EXTENSION_WILDCARD,
];

const MIME_TYPE_WILDCARD_TO_MIMETYPE_MAPPINGS = {
  [IMAGE_EXTENSION_WILDCARD]: supportImageMimeTypes,
  [VIDEO_EXTENSION_WILDCARD]: supportedVideoMimeTypes,
  [AUDIO_EXTENSION_WILDCARD]: supportedAudioMimeTypes,
};

// TODO: Come back and maybe replace these mappings with the mime-types npm package
// https://github.com/jshttp/mime-types#readme
// We'd have to tweak it slightly to our use case to also return GSuite mime types
// when provided with certain extensions like .docx and .xlsx
export const SUPPORTED_EXTENSIONS_TO_MIMETYPE_MAPPINGS = {
  '.aac': ['audio/aac'],
  '.als': ['audio/x-ableton-live-set'],
  '.flp': ['application/vnd.adobe.fla'],
  '.m4a': ['audio/mp4'],
  '.mid': ['audio/midi'],
  '.mp3': ['audio/mpeg'],
  '.ogg': ['audio/ogg'],
  '.wav': ['audio/wav'],
  '.wma': ['audio/x-ms-wma'],
  '.bmp': ['image/bmp'],
  '.eps': ['application/postscript'],
  '.gif': ['image/gif'],
  '.heic': ['image/heic'],
  '.ico': ['image/vnd.microsoft.icon'],
  '.jfif': ['image/jpeg'],
  '.png': ['image/png'],
  '.psd': ['image/vnd.adobe.photoshop'],
  '.tif': ['image/tiff'],
  '.webp': ['image/webp'],
  '.3g2': ['video/3gpp2'],
  '.3gp': ['video/3gpp'],
  '.avi': ['video/x-msvideo'],
  '.flv': ['video/x-flv'],
  '.m4v': ['video/x-m4v'],
  '.mkv': ['video/x-matroska'],
  '.mov': ['video/quicktime'],
  '.mp4': ['video/mp4'],
  '.mpg': ['video/mpeg'],
  '.ogv': ['video/ogg'],
  '.wmv': ['video/x-ms-wmv'],
  '.pdf': ['application/pdf'],
  '.mpeg': ['audio/mpeg'],
  '.csv': ['text/csv'],
  '.doc': ['application/msword', 'application/vnd.google-apps.document'],
  '.docx': ['application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'application/vnd.google-apps.document'],
  '.epub': ['application/epub+zip'],
  '.key': ['application/vnd.apple.keynote'],
  '.mobi': ['application/x-mobipocket-ebook'],
  '.odt': ['application/vnd.oasis.opendocument.text'],
  '.ppt': ['application/vnd.ms-powerpoint', 'application/vnd.google-apps.presentation'],
  '.pptx': ['application/vnd.openxmlformats-officedocument.presentationml.presentation', 'application/vnd.google-apps.presentation'],
  '.rtf': ['application/rtf'],
  '.srt': ['application/x-subrip'],
  '.txt': ['text/plain'],
  '.xls': ['application/vnd.ms-excel', 'application/vnd.google-apps.spreadsheet'],
  '.xlsx': ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/vnd.google-apps.spreadsheet'],
  '.zip': ['application/zip'],
  '.gz': ['application/gzip'],
  '.css': ['text/css'],
  '.scss': ['text/x-scss'],
  '.js': ['application/javascript'],
  '.swf': ['application/x-shockwave-flash'],
  '.eot': ['application/vnd.ms-fontobject'],
  '.ttf': ['font/ttf'],
  '.woff': ['font/woff'],
  '.woff2': ['font/woff2'],
  '.ai': ['application/postscript'],
  '.dotx': ['application/vnd.openxmlformats-officedocument.wordprocessingml.template'],
  '.gdoc': ['application/vnd.google-apps.document'],
  '.gsheet': ['application/vnd.google-apps.spreadsheet'],
  '.numbers': ['application/vnd.apple.numbers'],
  '.odp': ['application/vnd.oasis.opendocument.presentation'],
  '.ods': ['application/vnd.oasis.opendocument.spreadsheet'],
  '.pages': ['application/vnd.apple.pages'],
  '.psfx': ['application/x-font-psf'],
  '.pub': ['application/x-mspublisher'],
  '.rar': ['application/vnd.rar'],
  '.rpp': ['application/vnd.reaper-project'],
  '.tiff': ['image/tiff'],
  '.unity': ['application/vnd.unity'],
  '.xlsb': ['application/vnd.ms-excel.sheet.binary.macroEnabled.12'],
  '.cer': ['application/pkix-cert'],
  '.p8': ['application/pkcs8'],
  '.p12': ['application/x-pkcs12'],
  '.json': ['application/json', 'application/vnd.google-apps.script'],
  '.mobileprovision': ['application/x-apple-aspen-config'],
};

const SUPPORTED_MIMETYPES = Object.values(SUPPORTED_EXTENSIONS_TO_MIMETYPE_MAPPINGS).flat();

export const convertExtensionsToMimeTypes = (extensions) => {
  const extensionsToMimeTypes = [];
  if (!extensions) return extensionsToMimeTypes;

  extensions.forEach((ext) => {
    if (SUPPORTED_EXTENSIONS_TO_MIMETYPE_MAPPINGS[ext]) {
      extensionsToMimeTypes.push(...SUPPORTED_EXTENSIONS_TO_MIMETYPE_MAPPINGS[ext]);
    } else if (EXTENSION_WILDCARDS.includes(ext)) {
      extensionsToMimeTypes.push(...MIME_TYPE_WILDCARD_TO_MIMETYPE_MAPPINGS[ext]);
    } else if (SUPPORTED_MIMETYPES.includes(ext)) {
      extensionsToMimeTypes.push(ext);
    }
  });

  return uniq(extensionsToMimeTypes);
};

export const expandMimeTypeWildcards = (mimeTypes) => {
  const expandedMimeTypes = [];
  if (!mimeTypes) return expandedMimeTypes;

  const mimeTypeWildcards = mimeTypes.filter((ext) => EXTENSION_WILDCARDS.includes(ext));
  const mimeTypesWithoutWildcards = mimeTypes.filter((ext) => !EXTENSION_WILDCARDS.includes(ext));

  if (mimeTypeWildcards) {
    mimeTypeWildcards.forEach((wildcard) => {
      expandedMimeTypes.push(...MIME_TYPE_WILDCARD_TO_MIMETYPE_MAPPINGS[wildcard]);
    });
  }

  mimeTypesWithoutWildcards.forEach((mimeType) => {
    if (SUPPORTED_MIMETYPES.includes(mimeType)) {
      expandedMimeTypes.push(mimeType);
    }
  });

  return uniq(expandedMimeTypes);
};

// Typically, we configure the allowed file types for an uploader instance by passing
// in a list of file extensions. However, there are a handful of cases where we pass
// in MIME types instead. To allow for this, we need to convert any provided extensions
// to their corresponding MIME types and add them to any other MIME types that
// have been provided
const FileTypesConfig = (props = {}) => {
  const passedExtensions = props.extensions?.split(',').map((s) => s.trim()) || [];
  let permittedExtensions = [];
  const permittedTypes = [];

  passedExtensions.forEach((x) => {
    Object.keys(permissibleTypes).forEach((type) => {
      if (permissibleTypes[type].includes(x)) {
        permittedTypes.push(type);
        permittedExtensions.push(x);
      }
    });
  });

  permittedExtensions = uniq(permittedExtensions);
  if (!permittedExtensions.length) {
    permittedExtensions = null;
  }

  const permittedMimeTypesForGooglePicker = convertExtensionsToMimeTypes(permittedExtensions);

  // The Companion Client accepts either a list of permitted extensions or MIME types
  // for its allowedFileTypes configuration https://uppy.io/docs/uppy/#restrictions
  // Since the GooglePicker API explicitly requires MIME types to restrict the types
  // of files that it displays, we need to map the list of permitted exetensions passed
  // in to the uploader config into their correponsing MIME types.
  return {
    filetypes: uniq(permittedTypes),
    extensions: permittedExtensions, // return null or array
    permittedMimeTypesForGooglePicker,
  };
};

export default FileTypesConfig;
